import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useHolidays } from './useHolidays';

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// datepicker.html 색상 토큰
const POINT = 'var(--dk-accent, #0066CC)'; // 선택 endpoint
const RANGE_BG = 'var(--dk-accent-tint, #E0EDF9)'; // 선택/기간 셀 배경 (Blue 12%)
const HOVER_CELL = 'var(--dk-accent-soft, #EBF3FB)';
const HOVER_SPAN = 'var(--dk-accent-tint, #D6E7F7)';
const HOVER_ACCENT = '#3385D6';
const SUNDAY = 'var(--dk-danger, #F55060)';
const DISABLED = 'var(--dk-text-disabled, #D1D8E2)';
const SUN_DISABLED = 'var(--dk-danger-line, #FDD5D9)';
const SAT_DISABLED = 'var(--dk-accent-line, #C2DAF3)';
const BORDER = 'var(--dk-line, #E7E9EC)';
// 다크 색은 var(--dk-*) — 앱 다크 모드(styles/appDarkMode.ts). SVG 속성(stroke=)에는 var() 를 못 쓰므로 원래 색 그대로 (전역 SVG 규칙이 바꿈)

type Props = {
  /** 'YYYY-MM-DD' */
  startDate: string;
  /** 'YYYY-MM-DD' */
  endDate: string;
  onChange: (start: string, end: string) => void;
  /**
   * 'range'(기본): 연차 신청용 기간 선택 (시작~종료)
   * 'single': 단일 날짜 선택 (클릭한 날짜로 start=end, 즉시 확정)
   */
  mode?: 'range' | 'single';
  /** 'YYYY-MM-DD' — 이 날짜 이전은 비활성화 (단일 날짜 선택 시 과거 차단용) */
  minDate?: string;
  /** 'YYYY-MM-DD' — 이 날짜 이후는 비활성화 */
  maxDate?: string;
};

/**
 * 연차 신청 - 신청 기간 선택용 인라인 range 캘린더.
 * datepicker.html 사양을 따른다.
 * - 헤더: 박스형 이전/다음 + 년·월 드롭다운
 * - 1차 클릭: 시작점 / 2차 클릭: 종료점(역순이면 swap) / 3차 클릭: 새 시작점
 * - 선택/기간 셀은 #E0EDF9 배경 + 주 단위로 양끝 라운딩, endpoint 는 #0066CC 채움
 * - 일요일·공휴일 빨강 / 토요일 파랑 / 당월 외 비활성
 */
const LeaveCalendar = ({
  startDate,
  endDate,
  onChange,
  mode = 'range',
  minDate,
  maxDate,
}: Props) => {
  const { t } = useTranslation();
  const [viewMonth, setViewMonth] = useState<Dayjs>(() =>
    dayjs(startDate || undefined).startOf('month'),
  );
  // false = 다음 클릭이 새 시작점 / true = 다음 클릭이 종료점
  const [awaitingEnd, setAwaitingEnd] = useState(false);
  const holidays = useHolidays();

  const handleClick = (d: Dayjs) => {
    const dateStr = d.format('YYYY-MM-DD');
    // 단일 날짜 모드: 클릭한 날짜로 즉시 확정 (start=end)
    if (mode === 'single') {
      onChange(dateStr, dateStr);
      return;
    }
    if (!awaitingEnd) {
      onChange(dateStr, dateStr);
      setAwaitingEnd(true);
      return;
    }
    // 종료 선택: 시작보다 빠르면 swap
    if (d.isBefore(dayjs(startDate), 'day')) {
      onChange(dateStr, startDate);
    } else {
      onChange(startDate, dateStr);
    }
    setAwaitingEnd(false);
  };

  // 그리드: 당월 1일이 속한 주(일요일 시작)부터, 최소 5주 채움
  const firstOfMonth = viewMonth.startOf('month');
  const gridStart = firstOfMonth.subtract(firstOfMonth.day(), 'day');
  const daysInMonth = viewMonth.daysInMonth();
  const totalCells = Math.max(
    35,
    Math.ceil((firstOfMonth.day() + daysInMonth) / 7) * 7,
  );
  const cells: Dayjs[] = [];
  for (let i = 0; i < totalCells; i += 1) cells.push(gridStart.add(i, 'day'));
  const weeks: Dayjs[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  // 연차 신청은 사전 신청 위주라 올해~내년만 노출
  const baseYear = dayjs().year();
  const yearOptions = [baseYear, baseYear + 1];
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // 월 이동 범위도 연도 범위(첫 해 1월 ~ 마지막 해 12월)로 제한
  // minDate 가 있으면 그 달 이전으로는 이동하지 않도록 하한을 올린다.
  const yearMinMonth = dayjs(`${yearOptions[0]}-01-01`).startOf('month');
  const minMonth =
    minDate && dayjs(minDate).startOf('month').isAfter(yearMinMonth)
      ? dayjs(minDate).startOf('month')
      : yearMinMonth;
  const yearMaxMonth = dayjs(
    `${yearOptions[yearOptions.length - 1]}-12-01`,
  ).startOf('month');
  const maxMonth =
    maxDate && dayjs(maxDate).startOf('month').isBefore(yearMaxMonth)
      ? dayjs(maxDate).startOf('month')
      : yearMaxMonth;
  const canGoPrev = viewMonth.isAfter(minMonth, 'month');
  const canGoNext = viewMonth.isBefore(maxMonth, 'month');

  return (
    <Box
      sx={{
        padding: '16px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <NavButton
            direction="prev"
            label={t('이전 달')}
            disabled={!canGoPrev}
            onClick={() => setViewMonth((m) => m.subtract(1, 'month'))}
          />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
            }}
          >
            <TitlePicker
              value={viewMonth.year()}
              label={t('{{year}}년', { year: viewMonth.year() })}
              options={yearOptions.map((y) => ({
                value: y,
                label: t('{{year}}년', { year: y }),
              }))}
              onSelect={(y) => setViewMonth((m) => m.year(Number(y)))}
            />
            <TitlePicker
              value={viewMonth.month() + 1}
              label={t('{{month}}월', {
                month: String(viewMonth.month() + 1).padStart(2, '0'),
              })}
              options={monthOptions.map((mo) => ({
                value: mo,
                label: t('{{month}}월', { month: String(mo).padStart(2, '0') }),
              }))}
              onSelect={(mo) => setViewMonth((m) => m.month(Number(mo) - 1))}
            />
          </Box>
          <NavButton
            direction="next"
            label={t('다음 달')}
            disabled={!canGoNext}
            onClick={() => setViewMonth((m) => m.add(1, 'month'))}
          />
        </Box>

        {/* 요일 */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WEEKDAY_LABELS.map((label, i) => (
            <Box
              key={label}
              sx={{
                padding: '8px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '24px',
                color:
                  i === 0 ? SUNDAY : i === 6 ? POINT : 'var(--dk-text, #000)',
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* 날짜 그리드 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          rowGap: '8px',
        }}
      >
        {weeks.map((week) =>
          week.map((d) => (
            <DayCell
              key={d.format('YYYY-MM-DD')}
              day={d}
              viewMonthIndex={viewMonth.month()}
              startDate={startDate}
              endDate={endDate}
              holidays={holidays}
              minDate={minDate}
              maxDate={maxDate}
              onClick={handleClick}
            />
          )),
        )}
      </Box>
    </Box>
  );
};

const DayCell = ({
  day,
  viewMonthIndex,
  startDate,
  endDate,
  holidays,
  minDate,
  maxDate,
  onClick,
}: {
  day: Dayjs;
  viewMonthIndex: number;
  startDate: string;
  endDate: string;
  holidays: Set<string>;
  minDate?: string;
  maxDate?: string;
  onClick: (d: Dayjs) => void;
}) => {
  const key = day.format('YYYY-MM-DD');
  const dow = day.day();
  const isOther = day.month() !== viewMonthIndex;
  const isStart = key === startDate;
  const isEnd = key === endDate;
  // 선택/기간은 당월 외(이전달/다음달) 날짜도 동일하게 활성 표시한다.
  const isSelected = isStart || isEnd;
  const inRange = key > startDate && key < endDate;
  const filled = isSelected || inRange;
  const holiday = holidays.has(key);
  // 당월 외 또는 minDate 이전 / maxDate 이후 날짜는 회색(disabled) 스타일.
  // 단, 선택/기간에 포함된 날짜는 회색 처리하지 않고 활성으로 보이게 한다.
  const beforeMin = minDate ? key < minDate : false;
  const afterMax = maxDate ? key > maxDate : false;
  const disabled = (isOther || beforeMin || afterMax) && !filled;
  // 실제 클릭 차단은 min/max 제약에만 적용.
  // 이전달/다음달(overflow) 날짜는 회색 스타일은 유지하되 클릭 선택은 허용한다.
  const clickBlocked = beforeMin || afterMax;

  // 셀 배경 + 라운딩
  let radius = '0';
  if (isSelected) {
    if (isStart && isEnd) radius = '8px';
    else if (isStart) radius = '8px 0 0 8px';
    else radius = '0 8px 8px 0';
  } else if (inRange) {
    if (dow === 0) radius = '8px 0 0 8px';
    else if (dow === 6) radius = '0 8px 8px 0';
  }

  // 숫자 색 (일요일·공휴일 빨강 / 토요일 파랑)
  let color: string;
  if (disabled) {
    color =
      dow === 0 || holiday ? SUN_DISABLED : dow === 6 ? SAT_DISABLED : DISABLED;
  } else if (isSelected) {
    color = '#fff';
  } else {
    color =
      dow === 0 || holiday
        ? SUNDAY
        : dow === 6
        ? POINT
        : 'var(--dk-text, #000)';
  }

  const fontWeight = isSelected ? 700 : inRange ? 500 : 400;
  const isDefault = !disabled && !filled;

  return (
    <Box
      component="button"
      type="button"
      disabled={clickBlocked}
      onClick={() => onClick(day)}
      sx={{
        position: 'relative',
        height: '52px',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: filled ? RANGE_BG : 'transparent',
        borderRadius: radius,
        color,
        fontFamily: 'inherit',
        fontSize: '16px',
        fontWeight,
        lineHeight: '24px',
        cursor: clickBlocked ? 'default' : 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        ...(isDefault && {
          '&:hover': {
            background: HOVER_CELL,
            borderRadius: '8px',
            '& > span': {
              background: HOVER_SPAN,
              color: HOVER_ACCENT,
              outline: `1px solid ${HOVER_ACCENT}`,
              outlineOffset: '-1px',
            },
          },
        }),
      }}
    >
      <Box
        component="span"
        sx={{
          width: '100%',
          minWidth: '36px',
          height: '100%',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: isSelected ? '4px' : '4px',
          background: isSelected ? POINT : 'transparent',
        }}
      >
        {day.date()}
      </Box>
    </Box>
  );
};

const NavButton = ({
  direction,
  label,
  onClick,
  disabled,
}: {
  direction: 'prev' | 'next';
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    sx={{
      flexShrink: 0,
      padding: '4px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '4px',
      outline: `1px solid ${BORDER}`,
      outlineOffset: '-1px',
      background: disabled
        ? 'var(--dk-bg-subtle, #F9FAFB)'
        : 'var(--dk-bg, #fff)',
      cursor: disabled ? 'default' : 'pointer',
      ...(!disabled && {
        '&:hover': {
          background: 'var(--dk-bg-subtle, #F9FAFB)',
          outlineColor: 'var(--dk-text-strong, #4D5157)',
        },
        '&:active': {
          background: 'var(--dk-bg-muted, #F3F5F6)',
          outlineColor: 'var(--dk-text-primary, #34383D)',
        },
      }),
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 20 20"
      fill="none"
      sx={{
        width: '20px',
        height: '20px',
        display: 'block',
        transform: direction === 'prev' ? 'rotate(180deg)' : 'none',
      }}
    >
      <path
        d="M8.33008 5.83301L12.4967 9.99967L8.33008 14.1663"
        stroke={disabled ? '#D1D8E2' : 'black'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  </Box>
);

const TitlePicker = ({
  value,
  label,
  options,
  onSelect,
}: {
  value: number;
  label: string;
  options: { value: number; label: string }[];
  onSelect: (value: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          border: 'none',
          borderRadius: '4px',
          background: 'transparent',
          color: 'var(--dk-text, #000)',
          fontFamily: 'inherit',
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '24px',
          cursor: 'pointer',
        }}
      >
        <Box component="span" sx={{ padding: '0 2px' }}>
          {label}
        </Box>
        <Box
          component="svg"
          viewBox="0 0 24 24"
          fill="none"
          sx={{
            width: '24px',
            height: '24px',
            flex: 'none',
            transition: 'transform 0.12s ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <path
            d="M7 10L12 15L17 10"
            stroke="black"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </Box>
      {open && (
        <Box
          role="listbox"
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            width: '104px',
            maxHeight: '196px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            padding: '8px',
            overflowY: 'auto',
            borderRadius: '8px',
            outline: '1px solid var(--dk-text, #000)',
            outlineOffset: '-1px',
            background: 'var(--dk-bg, #fff)',
            boxShadow: '0 16px 32px rgba(0, 0, 0, 0.08)',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {options.map((opt) => {
            const selected = opt.value === value;
            return (
              <Box
                key={opt.value}
                component="button"
                type="button"
                onClick={() => {
                  onSelect(opt.value);
                  setOpen(false);
                }}
                sx={{
                  width: '100%',
                  minHeight: '28px',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '4px',
                  background: selected
                    ? 'var(--dk-bg-subtle, #F9FAFB)'
                    : 'var(--dk-bg, #fff)',
                  color: 'var(--dk-text, #000)',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  fontWeight: selected ? 700 : 400,
                  lineHeight: '20px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  '&:hover': {
                    background: 'var(--dk-bg-subtle, #F9FAFB)',
                    fontWeight: 500,
                  },
                }}
              >
                {opt.label}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default LeaveCalendar;
